/**
 * Spec Phase 4 Slice 4E — FrozenClaimsDock.
 * Phase 9 Slice 9B — pencil opens ProposeRevisionModal.
 *
 * Lists every frozen claim for the matter, in claim_number order.
 * The pencil affordance opens the propose-revision dialog targeting
 * the clicked claim. The dialog POSTs a spec_revision_proposals
 * row + redirects to Stage 3 (the architectural rule: NO
 * claims-side mutation from spec).
 *
 * Data source: matter-level fetch via getMatter(matterId), filtered
 * for is_frozen=true. Self-contained in this dock so the spec store
 * stays focused on spec-side state.
 */
import React, { useEffect, useState } from 'react';
import { Lock, Pencil, ChevronDown } from 'lucide-react';
import { getMatter } from '@/api/client';
import { useSpecStore } from '@/store/specStore';
import Dock from './Dock';
import ProposeRevisionModal, {
  type ProposeRevisionTargetClaim,
} from './ProposeRevisionModal';

interface FrozenClaim {
  id: string;
  claim_number: number;
  claim_text: string;
  is_frozen?: boolean;
  is_independent?: boolean;
}

export default function FrozenClaimsDock(): JSX.Element {
  const matterId = useSpecStore((s) => s.matterId);
  const [claims, setClaims] = useState<FrozenClaim[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [proposeTarget, setProposeTarget] =
    useState<ProposeRevisionTargetClaim | null>(null);

  useEffect(() => {
    if (!matterId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMatter(matterId)
      .then((m) => {
        if (cancelled) return;
        const all: FrozenClaim[] = (m?.claims ?? []) as FrozenClaim[];
        const frozen = all
          .filter((c) => c.is_frozen)
          .sort((a, b) => a.claim_number - b.claim_number);
        setClaims(frozen);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg =
          (e as { message?: string })?.message ?? 'Failed to load frozen claims.';
        setError(msg);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [matterId]);

  return (
    <Dock
      dockKey="frozenClaims"
      icon={<Lock className="h-3.5 w-3.5 text-zinc-500" aria-hidden />}
      title="Frozen claims"
      pillCount={claims.length}
      meta={loading ? 'Loading…' : error ?? undefined}
      testId="spec-dock-frozen-claims"
    >
      {!loading && !error && claims.length === 0 && (
        <p className="text-[11px] text-zinc-500">
          No frozen claims yet.
        </p>
      )}
      {proposeTarget && (
        <ProposeRevisionModal
          target={proposeTarget}
          onClose={() => setProposeTarget(null)}
        />
      )}
      {!loading && !error && claims.length > 0 && (
        <ul className="space-y-2">
          {claims.map((c) => (
            <FrozenClaimCard
              key={c.id}
              claim={c}
              onProposeRevision={() => setProposeTarget({
                kind: 'claim',
                claim_id: c.id,
                claim_number: c.claim_number,
                claim_text: c.claim_text,
              })}
            />
          ))}
        </ul>
      )}
    </Dock>
  );
}

/** BUG-035 — readable claim card with inline expand affordance.
 * Drafters complained the old text-[10px] line-clamp-3 was
 * unreadable on dense claims. We render the first 2 lines clamped
 * (still keeps the rail compact across 16 claims) and offer a
 * "Read more" toggle that expands the card in place — no modal,
 * no navigation. The Dock's 40vh max-height + overflow-y-auto
 * keeps the rail bounded even when several cards are expanded. */
function FrozenClaimCard({
  claim, onProposeRevision,
}: {
  claim: FrozenClaim;
  onProposeRevision: () => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState<boolean>(false);
  return (
    <li
      className="rounded-sm border border-zinc-200 bg-white p-2.5"
      data-testid={`spec-frozen-claim-${claim.claim_number}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono text-[11px] font-medium text-zinc-700">
          Claim {claim.claim_number}
          {claim.is_independent && (
            <span className="ml-1 rounded-sm bg-zinc-100 px-1 text-[9px] text-zinc-700">
              indep
            </span>
          )}
        </span>
        <button
          type="button"
          className="text-zinc-400 hover:text-zinc-700"
          onClick={onProposeRevision}
          aria-label={`Propose revision to claim ${claim.claim_number}`}
          title="Propose a revision (won't edit the claim — sends a banner to Stage 3)"
          data-testid={`spec-frozen-claim-pencil-${claim.claim_number}`}
        >
          <Pencil className="h-3 w-3" />
        </button>
      </div>
      <p
        className={[
          'mt-1.5 text-xs leading-relaxed text-zinc-800 whitespace-pre-wrap',
          expanded ? '' : 'line-clamp-2',
        ].join(' ')}
      >
        {claim.claim_text}
      </p>
      <button
        type="button"
        className="mt-1 inline-flex items-center gap-0.5 text-[10px] font-medium text-[#002FA7] hover:text-[#001f6f]"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`spec-frozen-claim-toggle-${claim.claim_number}`}
        aria-expanded={expanded}
      >
        <ChevronDown
          className={[
            'h-3 w-3 transition-transform',
            expanded ? 'rotate-180' : '',
          ].join(' ')}
          aria-hidden
        />
        {expanded ? 'Show less' : 'Read more'}
      </button>
    </li>
  );
}
